/**
 * Pizza slider (template-parts/blocks/block-pizza-slider.php)
 *
 * The server renders a plain list of pizzas; this module turns it into an
 * endless cover-flow slider: Swiper in loop mode with centred slides, where
 * each pizza grows smoothly as it approaches the centre and only the centred
 * one shows its name and ingredients.
 *
 *   desktop  horizontal, 0.5 1 [1] 1 0.5 visible
 *   mobile   vertical,   0.5 [1] 0.5 visible (below the medium breakpoint),
 *            spanning the whole screen under the transparent bars
 *
 * Swiper 14 can switch `direction` through `breakpoints`, but a switch with
 * loop mode re-creates the loop in place; rebuilding the instance on a
 * matchMedia change is simpler and leaves no stale inline sizes behind.
 *
 * Easy mode keeps the server-rendered list (the page is remounted when it
 * toggles), so there is nothing to do there.
 */
import Swiper from 'swiper'
import { A11y, Keyboard, Mousewheel } from 'swiper/modules'
import 'swiper/css'
import 'swiper/css/a11y'
import { onPage } from '../app/lifecycle'
import { getState } from '../app/store'

// Loop mode needs slides beyond the visible ones on both sides.
const MIN_SLIDES = 10
const DESKTOP_QUERY = '(min-width: 640px)'
const REDUCED_MOTION_QUERY = '(prefers-reduced-motion: reduce)'
const SPEED = 650
const A11Y_ATTRIBUTES = ['role', 'aria-label', 'aria-roledescription']
const FOCUSABLE = 'a[href], button, input, select, textarea, [tabindex]'

onPage((root) => {
  if (getState().easyMode) return

  const cleanups = [...root.querySelectorAll('[data-pizza-slider]')].map(mountSlider)
  return () => cleanups.forEach((cleanup) => cleanup())
})

function mountSlider(block) {
  const viewport = block.querySelector('[data-pizza-slider-viewport]')
  const track = viewport?.querySelector('[data-pizza-slider-track]')
  const originals = track ? [...track.querySelectorAll(':scope > [data-pizza-slider-slide]')] : []
  if (!viewport || !originals.length) return () => {}

  const labels = {
    prev: 'Previous slide',
    next: 'Next slide',
    slide: '{{index}} / {{slidesLength}}',
    ...parseLabels(block.dataset.pizzaSliderLabels),
  }
  const clones = fillLoop(track, originals)
  const slides = [...originals, ...clones]
  const status = createStatus(block)
  const desktop = window.matchMedia(DESKTOP_QUERY)
  const reducedMotion = window.matchMedia(REDUCED_MOTION_QUERY)

  viewport.classList.add('swiper')
  track.classList.add('swiper-wrapper')
  slides.forEach((slide) => slide.classList.add('swiper-slide'))

  let swiper = null
  let maxScale = 1

  const readMaxScale = () => {
    maxScale = readNumber(block, '--pizza-scale-max', 1.6)
  }

  const build = (initialSlide = 0) => {
    const isDesktop = desktop.matches
    readMaxScale()
    swiper = new Swiper(viewport, {
      modules: [A11y, Keyboard, Mousewheel],
      direction: isDesktop ? 'horizontal' : 'vertical',
      slidesPerView: isDesktop ? 4 : 2,
      centeredSlides: true,
      loop: true,
      initialSlide,
      speed: reducedMotion.matches ? 0 : SPEED,
      watchSlidesProgress: true,
      slideToClickedSlide: true,
      grabCursor: true,
      roundLengths: true,
      keyboard: { enabled: true, onlyInViewport: true },
      mousewheel: { enabled: true, forceToAxis: false, sensitivity: 1 },
      a11y: {
        enabled: true,
        prevSlideMessage: labels.prev,
        nextSlideMessage: labels.next,
        // Swiper counts the copies too ("1 von 12" for 4 pizzas). The originals
        // come first in the DOM, so their {{index}} is right; only the total
        // needs pinning to the real number of pizzas (copies are aria-hidden).
        slideLabelMessage: labels.slide.replaceAll('{{slidesLength}}', String(originals.length)),
        // Keep the <ul>/<li> list semantics instead of role="group" slides.
        slideRole: 'listitem',
        // Loop mode moves slides around in the DOM; a live wrapper would read
        // out whichever slide was moved. The status line below announces the
        // centred pizza instead.
        wrapperLiveRegion: false,
      },
      on: {
        afterInit: (instance) => updateScales(instance, maxScale),
        setTranslate: (instance) => updateScales(instance, maxScale),
        resize: (instance) => {
          readMaxScale()
          updateScales(instance, maxScale)
        },
        setTransition: updateTransition,
        slideChangeTransitionEnd: (instance) => announce(instance, status),
      },
    })
  }

  const rebuild = () => {
    const realIndex = swiper?.realIndex ?? 0
    swiper?.destroy(true, true)
    build(realIndex)
  }

  const onSpeedPreference = () => {
    if (swiper) swiper.params.speed = reducedMotion.matches ? 0 : SPEED
  }

  const cleanup = () => {
    desktop.removeEventListener('change', rebuild)
    reducedMotion.removeEventListener('change', onSpeedPreference)
    swiper?.destroy(true, true)
    swiper = null

    // Hand the list back exactly as the server rendered it.
    clones.forEach((clone) => clone.remove())
    originals.forEach((slide) => {
      slide.classList.remove('swiper-slide')
      A11Y_ATTRIBUTES.forEach((name) => slide.removeAttribute(name))
    })
    track.classList.remove('swiper-wrapper')
    track.removeAttribute('id')
    viewport.classList.remove('swiper')
    status.remove()
    block.classList.remove('is-slider')
  }

  // Slider layout first: Swiper measures the container when it initialises.
  block.classList.add('is-slider')
  try {
    build()
  } catch (error) {
    // Leave the readable list rather than a half-built slider.
    console.error('[pizza-slider] init failed', error)
    cleanup()
    block.classList.add('is-static')
    return () => block.classList.remove('is-static')
  }

  desktop.addEventListener('change', rebuild)
  reducedMotion.addEventListener('change', onSpeedPreference)

  return cleanup
}

/**
 * Cover-flow scaling: slide.progress is 0 for the centred slide and ±1 for its
 * neighbours (watchSlidesProgress). Scale, caption opacity and stacking follow
 * a smoothstep of that distance, so pizzas grow gradually while dragging.
 * --pizza-focus (0…1) is that smoothstep itself; the vertical slider lifts
 * the centre pizza by it (85_blocks.sass).
 */
function updateScales(swiper, maxScale) {
  swiper.slides.forEach((slide) => {
    // progress is NaN while the slider has no size yet (e.g. a hidden tab)
    const progress = Number.isFinite(slide.progress) ? slide.progress : 1
    const distance = Math.min(Math.abs(progress), 1)
    const focus = smoothstep(1 - distance)

    slide.style.setProperty('--pizza-focus', focus.toFixed(4))
    slide.style.setProperty('--pizza-scale', (1 + (maxScale - 1) * focus).toFixed(4))
    slide.style.setProperty('--pizza-caption-opacity', Math.max(0, (focus - 0.6) / 0.4).toFixed(3))
    slide.style.zIndex = String(1 + Math.round(focus * 10))
  })
}

/** Animate the scale in step with the wrapper translate (0 while dragging). */
function updateTransition(swiper, duration) {
  swiper.slides.forEach((slide) => slide.style.setProperty('--pizza-duration', `${duration}ms`))
}

function announce(swiper, status) {
  const slide = swiper.slides[swiper.activeIndex]
  const title = slide?.querySelector('[data-pizza-slider-title]')?.textContent.trim()
  if (!title) return

  const diet = slide.querySelector('[data-pizza-slider-diet]')?.textContent.trim()
  status.textContent = diet ? `${title}, ${diet}` : title
}

/**
 * Repeat the list until the loop has enough slides. Copies are hidden from
 * assistive tech and the structured data only describes the originals.
 *
 * Not `inert`: an inert node is skipped by hit-testing (like pointer-events:
 * none), so a click on a copy would land on the wrapper and Swiper's
 * slideToClickedSlide could never centre it. aria-hidden is enough while the
 * slides hold nothing focusable; anything focusable added later is taken out
 * of the tab order here.
 */
function fillLoop(track, originals) {
  const clones = []
  while (originals.length + clones.length < MIN_SLIDES) {
    originals.forEach((slide) => {
      const clone = slide.cloneNode(true)
      clone.setAttribute('aria-hidden', 'true')
      clone.querySelectorAll(FOCUSABLE).forEach((el) => el.setAttribute('tabindex', '-1'))
      clone.dataset.pizzaSliderClone = ''
      // A copy taken mid-load would keep lazysizes' in-flight class forever.
      clone.querySelectorAll('img.lazyloading').forEach((img) => {
        img.classList.replace('lazyloading', 'lazyload')
      })
      clones.push(clone)
    })
  }
  track.append(...clones)
  return clones
}

function createStatus(block) {
  const status = document.createElement('p')
  status.className = 'is-hidden-sr'
  status.setAttribute('aria-live', 'polite')
  status.setAttribute('aria-atomic', 'true')
  block.append(status)
  return status
}

function parseLabels(json) {
  try {
    return JSON.parse(json ?? '{}') ?? {}
  } catch {
    return {}
  }
}

function readNumber(el, property, fallback) {
  const value = parseFloat(getComputedStyle(el).getPropertyValue(property))
  return Number.isFinite(value) ? value : fallback
}

function smoothstep(t) {
  return t * t * (3 - 2 * t)
}
